/**
 * Ad text checks before anything reaches the owner (SPEC §6 step 2). Google rejects text that
 * breaks these published limits and editorial rules, so a proposal is validated up front
 * rather than failing at launch. Counts are in Unicode code points (Arabic letters and
 * diacritics count one each; Google only double-counts CJK, which we do not use).
 */

export const ASSET_LIMITS = {
  /** Performance Max (Maps / local sales). */
  pmax: {
    headlines: { min: 3, max: 15, chars: 30 },
    longHeadlines: { min: 1, max: 5, chars: 90 },
    descriptions: { min: 2, max: 5, chars: 90, shortChars: 60 },
    businessNameChars: 25,
  },
  /** Responsive search ad (brand search campaign). */
  search: {
    headlines: { min: 3, max: 15, chars: 30 },
    descriptions: { min: 2, max: 4, chars: 90 },
    pathChars: 15,
    paths: 2,
  },
} as const;

export interface PmaxAssets {
  readonly format: 'pmax';
  readonly businessName: string;
  readonly headlines: readonly string[];
  readonly longHeadlines: readonly string[];
  readonly descriptions: readonly string[];
}

export interface SearchAssets {
  readonly format: 'search';
  readonly headlines: readonly string[];
  readonly descriptions: readonly string[];
  readonly paths: readonly string[];
}

export type AdAssets = PmaxAssets | SearchAssets;

export interface AssetProblem {
  readonly field: string;
  readonly index: number | null;
  readonly rule:
    | 'count'
    | 'length'
    | 'empty'
    | 'whitespace'
    | 'duplicate'
    | 'exclamation_in_headline'
    | 'emoji'
    | 'repeated_punctuation'
    | 'all_caps'
    | 'short_description_missing'
    | 'path_characters';
}

const EMOJI = /\p{Extended_Pictographic}/u;
const REPEATED_PUNCTUATION = /([!?.,،؟])\1/u;
const LATIN_LETTERS = /[A-Za-z]/g;
const PATH_CHARACTERS = /^[\p{L}\p{N}-]+$/u;

export function charCount(text: string): number {
  return text.match(/./gsu)?.length ?? 0;
}

export function validateAssets(assets: AdAssets): AssetProblem[] {
  const problems: AssetProblem[] = [];
  if (assets.format === 'pmax') {
    const limits = ASSET_LIMITS.pmax;
    checkText(problems, 'businessName', null, assets.businessName, limits.businessNameChars, false);
    checkList(problems, 'headlines', assets.headlines, limits.headlines, true);
    checkList(problems, 'longHeadlines', assets.longHeadlines, limits.longHeadlines, true);
    checkList(problems, 'descriptions', assets.descriptions, limits.descriptions, false);
    if (!assets.descriptions.some((d) => charCount(d.trim()) <= limits.descriptions.shortChars)) {
      problems.push({ field: 'descriptions', index: null, rule: 'short_description_missing' });
    }
  } else {
    const limits = ASSET_LIMITS.search;
    checkList(problems, 'headlines', assets.headlines, limits.headlines, true);
    checkList(problems, 'descriptions', assets.descriptions, limits.descriptions, false);
    if (assets.paths.length > limits.paths) {
      problems.push({ field: 'paths', index: null, rule: 'count' });
    }
    assets.paths.forEach((path, index) => {
      checkText(problems, 'paths', index, path, limits.pathChars, false);
      if (path !== '' && !PATH_CHARACTERS.test(path)) {
        problems.push({ field: 'paths', index, rule: 'path_characters' });
      }
    });
  }
  return problems;
}

function checkList(
  problems: AssetProblem[],
  field: string,
  items: readonly string[],
  limits: { readonly min: number; readonly max: number; readonly chars: number },
  isHeadline: boolean,
): void {
  if (items.length < limits.min || items.length > limits.max) {
    problems.push({ field, index: null, rule: 'count' });
  }
  const seen = new Set<string>();
  items.forEach((item, index) => {
    checkText(problems, field, index, item, limits.chars, isHeadline);
    const key = item.trim().toLocaleLowerCase();
    if (key !== '' && seen.has(key)) problems.push({ field, index, rule: 'duplicate' });
    seen.add(key);
  });
}

function checkText(
  problems: AssetProblem[],
  field: string,
  index: number | null,
  text: string,
  maxChars: number,
  isHeadline: boolean,
): void {
  if (text.trim() === '') {
    problems.push({ field, index, rule: 'empty' });
    return;
  }
  if (text !== text.trim() || /\s{2,}/u.test(text))
    problems.push({ field, index, rule: 'whitespace' });
  if (charCount(text) > maxChars) problems.push({ field, index, rule: 'length' });
  if (isHeadline && /[!！]/u.test(text)) {
    problems.push({ field, index, rule: 'exclamation_in_headline' });
  }
  if (EMOJI.test(text)) problems.push({ field, index, rule: 'emoji' });
  if (REPEATED_PUNCTUATION.test(text))
    problems.push({ field, index, rule: 'repeated_punctuation' });
  const latin = text.match(LATIN_LETTERS) ?? [];
  if (latin.length >= 4 && latin.every((c) => c === c.toUpperCase()) && text.includes(' ')) {
    problems.push({ field, index, rule: 'all_caps' });
  }
}

/**
 * Image sizes for Performance Max assets, as Google publishes them (recommended / minimum,
 * in pixels; files up to 5 MB). Shown on photo briefs for the design team; re-check against
 * Google's asset guide before the first launch, as the guide changes.
 */
export const IMAGE_SPECS = {
  landscape: { ratio: '1.91:1', recommended: '1200×628', minimum: '600×314' },
  square: { ratio: '1:1', recommended: '1200×1200', minimum: '300×300' },
  portrait: { ratio: '4:5', recommended: '960×1200', minimum: '480×600' },
} as const;
export type ImageOrientation = keyof typeof IMAGE_SPECS;
export const IMAGE_ORIENTATIONS = Object.keys(IMAGE_SPECS) as ImageOrientation[];
